import { TriangleAlert } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "./overlay.js";
import { Button } from "./primitives.js";

/**
 * Confirmation for actions that cannot be undone: it lists what will happen
 * and, when `confirmText` is given, asks the user to type it (usually the
 * resource name) before the button unlocks.
 */
export function DangerDialog({
  open,
  onOpenChange,
  title,
  consequences,
  confirmText,
  actionLabel,
  busy = false,
  disabled = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequences: ReactNode[];
  confirmText?: string;
  actionLabel: string;
  busy?: boolean;
  /** Keeps the action locked for a reason shown in `children` (e.g. nothing chosen yet). */
  disabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const unlocked = confirmText === undefined || typed.trim() === confirmText;
  const close = (next: boolean) => {
    if (!next) setTyped("");
    onOpenChange(next);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger-solid"
            disabled={!unlocked || disabled}
            busy={busy}
            onClick={onConfirm}
          >
            {actionLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex gap-3 rounded-lg bg-danger-soft px-4 py-3 text-danger">
          <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
          <ul className="flex list-disc flex-col gap-1 pl-4">
            {consequences.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
        {children}
        {confirmText !== undefined && (
          <label className="flex flex-col gap-1.5">
            <span>
              {t("danger.typeToConfirm")} <strong className="font-mono">{confirmText}</strong>
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="min-h-10 rounded-lg border border-line bg-surface px-3 font-mono text-sm focus:border-danger"
            />
          </label>
        )}
      </div>
    </Dialog>
  );
}
